import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import { createStripeOnboarding, getStripeAccountStatus } from "../../../helpers/paymentsApi";

const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_APP_STRIPE_PUBLISHABLE_KEY;

const StripeConnectContext = createContext(null);

/**
 * Shared Stripe Connect state for the whole Payments page. Stripe is the
 * source of truth for balances/payouts/account status — this only tracks
 * enough state locally (accountId + onboardingComplete) to decide which
 * Porcher UI to show; it never copies Stripe's financial data into
 * Firestore/React state itself (the embedded components render Stripe's
 * live data directly).
 *
 * IMPORTANT: account creation must stay opt-in for landlords who've never
 * connected a payout account. We only initialize Stripe Connect.js (which
 * calls createStripeOnboarding behind the scenes) once the caller already
 * has a connected account OR has explicitly clicked "Set up rent payouts"
 * — never automatically on page mount for a brand-new landlord.
 */
export const StripeConnectProvider = ({ children }) => {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [error, setError] = useState("");
  const [connectInstance, setConnectInstance] = useState(null);
  const [started, setStarted] = useState(false);

  const refreshStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const next = await getStripeAccountStatus();
      setStatus(next);
      // Already connected on a previous visit — no need to re-prompt for
      // opt-in, just resume showing onboarding/financial widgets.
      if (next?.connected) setStarted(true);
    } catch (e) {
      console.error("Load Stripe status error:", e);
      setError("Couldn't check your payout setup status.");
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  // Stripe Connect.js calls this whenever it needs a client secret — on
  // first load, and again to refresh an expiring Account Session. It never
  // creates a second account: the Lambda reuses the stored accountId if
  // one exists.
  const fetchClientSecret = useCallback(async () => {
    const { clientSecret } = await createStripeOnboarding();
    return clientSecret;
  }, []);

  useEffect(() => {
    if (!started) return;
    if (!STRIPE_PUBLISHABLE_KEY) return;
    if (statusLoading || connectInstance) return;

    try {
      setConnectInstance(
        loadConnectAndInitialize({
          publishableKey: STRIPE_PUBLISHABLE_KEY,
          fetchClientSecret,
          appearance: {
            overlays: "dialog",
            variables: { colorPrimary: "#556ee6" },
          },
        })
      );
    } catch (e) {
      console.error("Stripe Connect init error:", e);
      setError("Couldn't load your payout account information. Please try again.");
    }
  }, [started, statusLoading, connectInstance, fetchClientSecret]);

  const start = useCallback(() => setStarted(true), []);

  return (
    <StripeConnectContext.Provider
      value={{
        status,
        statusLoading,
        error,
        refreshStatus,
        connectInstance,
        started,
        start,
        publishableKeyMissing: !STRIPE_PUBLISHABLE_KEY,
      }}
    >
      {children}
    </StripeConnectContext.Provider>
  );
};

export const useStripeConnect = () => {
  const ctx = useContext(StripeConnectContext);
  if (!ctx) {
    throw new Error("useStripeConnect must be used within a StripeConnectProvider");
  }
  return ctx;
};
